import {Injectable} from '@angular/core';
import {Permissions} from '@models/permission.model';
import {HttpClient, HttpParams} from '@angular/common/http';
import {PermissionRole} from '@shared/models/permission.model';
import {GenericFunctionsUtil, IdName} from 'front-base-angular-core/angular/projects/front-base-angular-core/src/public-api';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  permissions: Permissions;
  tabs: IdName[];

  constructor(
    private http: HttpClient
  ) {
  }

  async init(): Promise<any> {
    this.permissions = await this.getPermissionsBack();
  }

  async getPermissions(): Promise<Permissions> {
    if (this.permissions) {
      return this.permissions;
    }

    this.permissions = await this.getPermissionsBack();
    return this.permissions;
  }

  getPermissionsSync(): Permissions {
    return this.permissions;
  }

  private getPermissionsBack(): Promise<Permissions> {
    const url = `/api/v1/auth/permits`;
    return this.http.get<Permissions>(url).toPromise();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasPermission(permissionKey: string): boolean {
    // TODO Uncomment when permissions are not available.
    //return true;
    const permissions = this.getPermissionsSync();
    const userHasPermission = permissions?.[permissionKey];
    if (!userHasPermission) {
      return false;
    }

    return userHasPermission;
  }

  hasPermissions(permissionKeys: string[], operation = 'and'): boolean {
    if (operation === 'and') {
      return permissionKeys.every((key) => this.hasPermission(key));
    }

    if (operation === 'or') {
      return !!permissionKeys.find((key) => this.hasPermission(key));
    }
  }

  resetPermissions(): void {
    this.permissions = null;
  }

  async getPermissionRoles(): Promise<IdName[]> {
    if (this.tabs) {
      return this.tabs;
    }

    const url = '/api/v1/authz/roles';
    this.tabs = await this.http.get<IdName[]>(url).toPromise();
    return this.tabs;
  }

  async getPermissionsFromRole(perRoleCode: string): Promise<PermissionRole[]> {
    const url = '/api/v1/authz/roles/permissions';
    const httpParams = new HttpParams().set('role', perRoleCode.toString());
    return this.http.get<PermissionRole[]>(url, {params: httpParams}).toPromise();
  }

  async getPermissionsFromAllRoles(): Promise<PermissionRole[]> {
    const url = '/api/v1/authz/roles/permissions';
    return this.http.get<PermissionRole[]>(url).toPromise();
  }

  async updatePermissionsFromRole(model: PermissionRole[]): Promise<any> {
    const url = '/api/v1/authz/roles/permissions';
    return this.http.put(url, model).toPromise();
  }

  async updatePermissionsFromAllRoles(model: PermissionRole[]): Promise<any> {
    const url = '/api/v1/authz/roles/permissions';
    return this.http.put(url, model).toPromise();
  }

  filterItemsByPermissions(originalItems: any[]): any {
    let items = GenericFunctionsUtil.deepClone(originalItems);
    items = items.filter(item => {
      if (!item.permissions) {
        return true;
      }

      return this.hasPermissions(item.permissions, item.permissionsOperation);
    });

    return items;
  }

  filterItemsByActions(originalItems: any[], actions: string[]): any {
    let items = GenericFunctionsUtil.deepClone(originalItems);
    items = items.filter(item => {
      if (!item.actions) {
        return true;
      }

      return this.hasActions(item.actions, actions, items.actionsOperation);
    });

    return items;
  }

  hasActions(actionKeys: string[], actions: string[], operation = 'and'): boolean {
    if (operation === 'and') {
      return actionKeys.every((key) => this.hasAction(key, actions));
    }

    if (operation === 'or') {
      return !!actionKeys.find((key) => this.hasAction(key, actions));
    }
  }

  hasAction(actionKey: string, actions: string[]): boolean {
    return actions?.includes(actionKey);
  }
}
